import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { INPUT_DATA } from '../Model/Models';
import HandleChange from '../../../../../Helpers/HandleChange';
import {
    AdvanceSearchByFilters,
    CreateHidInput,
    GetAllSiosByHidId,
    GetInputTypeConnections
} from '../../../../../reduxToolkit/HID/HidApi';
import { toast } from 'react-toastify';
import { errorMsg } from "../../../../../Apis/functions";
import { TABLES } from "../../../../../Apis/Tables";

/**
 * @Author: Karen Yunuen Pozos Arias
 * @JobTitle: Full Stack Developer - IBL
 * @Email: kpozos@ibl.mx
 *
 * Hook that manages the creation of a new HID input.
 * Handles form state, validation, API calls and field updates dynamically based on controller selection.
 */
const CreateInputApp = ({ onHide, controllerId = null, setModified }) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();

    // Redux selectors
    const controllers = useSelector((state) => state.HidSlice.advanceSearchByFilters);
    const sioIds = useSelector((state) => state.HidSlice.getAllSiosByHidId);
    const getInputTypeConnections = useSelector((state) => state.HidSlice.getInputTypeConnections);

    // Local state
    const [emptyFields, setEmptyFields] = useState(false);
    const [input, setInput] = useState({ ...INPUT_DATA, controllerId });

    // Custom change handler for form fields
    const { handleChange } = HandleChange({ model: input, setModel: setInput });

    useEffect(() => {
        dispatch(GetInputTypeConnections());
    }, [dispatch]);

    /**
     * Load all HID controllers if there is no controllerId preselected
     */
    useEffect(() => {
        if (!controllerId) {
            dispatch(AdvanceSearchByFilters({ option: TABLES.HID, body: {} }));
        }
    }, [controllerId]);

    /**
     * Load available SIOs (I/O interfaces) when a controller is selected
     */
    useEffect(() => {
        if (input.controllerId) {
            dispatch(GetAllSiosByHidId({ hidId: input.controllerId }));
        }
    }, [input.controllerId]);

    /**
     * Validate and submit input creation form
     * - Checks required fields
     * - Sends creation request
     * - Shows feedback messages
     */
    const handleSubmit = () => {
        if (!input.isValid()) {
            setEmptyFields(true);
            return;
        }

        const body = input.toAddEdit();
        dispatch(CreateHidInput(body)).then(
            ({ payload: { data: { success, code } } }) => {
                if (success === true) {
                    toast.success(t('has_been_created_successfully'));
                    resetValues(); // Reset form after success
                    setModified(true); // Notify parent of changes
                    onHide(); // Close modal or form
                } else errorMsg(code);
            }
        );
    };

    /**
     * Reset all local states and form fields
     */
    const resetValues = () => {
        setEmptyFields(false);
        setInput({ ...INPUT_DATA, controllerId });
    };

    return {
        t, // i18n translation instance
        resetValues, // Reset function for external use
        handleSubmit, // Submit handler
        input, // Current form data
        handleChange, // Input change handler
        emptyFields, // Field validation flag
        controllers, // Available controllers list
        sioIds, // Available SIOs for selected controller
        getInputTypeConnections
    };
};

export default CreateInputApp;
